import React, { useState } from 'react';
import { setUserBirthDate } from '../../services/gardenService';
import './Garden.css';

interface BirthDateSetupProps {
  userId: number;
  onComplete: () => void;
}

const BirthDateSetup: React.FC<BirthDateSetupProps> = ({ userId, onComplete }) => {
  const [birthDate, setBirthDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!birthDate) {
      setError('Please enter your birth date');
      return;
    }

    // Validate date format (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(birthDate)) {
      setError('Please enter a valid date in YYYY-MM-DD format');
      return;
    }

    // Validate date is not in the future
    const date = new Date(birthDate);
    const today = new Date();
    if (date > today) {
      setError('Birth date cannot be in the future');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await setUserBirthDate({
        user_id: userId,
        birth_date: birthDate
      });

      if (response.success) {
        onComplete();
      } else {
        setError(response.message || 'Failed to set birth date');
      }
    } catch (err) {
      console.error('Error setting birth date:', err);
      setError('Failed to set birth date');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="garden-birthdate-setup-overlay">
      <div className="garden-birthdate-setup-dialog">
        <h2 className="garden-birthdate-setup-title">Welcome to Your Life Garden</h2>
        <p className="garden-birthdate-setup-description">
          Your garden visualizes each day of your life as a plant. The size of each plant 
          represents your daily activity (tasks completed, habits done, and scheduled blocks).
        </p>
        <p className="garden-birthdate-setup-description">
          To get started, please enter your birth date. This helps us organize your days 
          into life years.
        </p>
        
        <form onSubmit={handleSubmit} className="garden-birthdate-setup-form">
          <div className="garden-birthdate-setup-field">
            <label htmlFor="birth-date" className="garden-birthdate-setup-label">
              Birth Date
            </label>
            <input
              id="birth-date"
              type="date"
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
              className="garden-birthdate-setup-input"
              max={new Date().toISOString().split('T')[0]}
              required
            />
          </div>

          {error && (
            <div className="garden-birthdate-setup-error">{error}</div>
          )}

          <div className="garden-birthdate-setup-actions">
            <button
              type="submit"
              className="garden-birthdate-setup-button"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Continue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BirthDateSetup;

